import { useState } from "react";
import bwipjs from "bwip-js";

const MM_TO_PX = 3.78;

function decodeEscapedName(name: string): string {
    return name
        .replace(/_x([0-9A-Fa-f]{4})_/g, (_, hex) =>
            String.fromCharCode(parseInt(hex, 16))
        )
        .replace(/_/g, " ")
        .replace(/\\&/g, "\n");
}

function wrapText(
    textElement: SVGTextElement | Element,
    textContent: string,
    maxWidthPx: number
) {
    const words = textContent.split(" ");
    let tspan = document.createElementNS("http://www.w3.org/2000/svg", "tspan");
    tspan.setAttribute("x", textElement.getAttribute("x")!);
    tspan.setAttribute("text-anchor", textElement.getAttribute("text-anchor")!);
    tspan.setAttribute("dy", "0");
    textElement.appendChild(tspan);
    let line = "";

    words.forEach((word) => {
        const testLine = line + word + " ";
        tspan.textContent = testLine;

        if (tspan.getComputedTextLength() > maxWidthPx && line.length > 0) {
            tspan.textContent = line.trim();
            line = word + " ";
            tspan = document.createElementNS("http://www.w3.org/2000/svg", "tspan");
            tspan.setAttribute("x", textElement.getAttribute("x")!);
            tspan.setAttribute("dy", "1.2em"); // Line height
            textElement.appendChild(tspan);
            tspan.textContent = line;
        } else {
            line = testLine;
        }
    });
    tspan.textContent = line.trim();
}

const PREVIEW_DATA: Record<string, string> = {
    ProductFamily: "MegaSlate®",
    ModuleType: "MegaSlate® II Black 6DM145",
    Version: "5",
    ArticleNumber: "31002987",
    PNominal: "145 Wp",
    Pmeas: "",
    Isc: "9.44 A",
    UoC: "20.3 V",
    MaxPPCurrent: "8.9 A",
    MaxPPVoltage: "16.7 V",
    MaxSytemVoltage: "1000 volt DC",
    MaxOCP: "18 A",
    ProductId: "603",
    ToleranceText1: "Tolerance for Pmax is +/- 3% and for Isc is +/- 3%",
    ToleranceText2:
        "Electrical rating at Standard Test Conditions (1000 W/m², 25 °C, AM 1.5 according to EN 60904-3)",
};

async function generateSvg(
    xmlString: string,
    data: Record<string, string>
): Promise<SVGSVGElement> {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlString, "text/xml");
    const label = xmlDoc.querySelector("ThermalLabel");
    if (!label) throw new Error("Tag ThermalLabel not found (.tl File not found).");

    const width = parseFloat(label.getAttribute("Width")!) * MM_TO_PX;
    const height = parseFloat(label.getAttribute("Height")!) * MM_TO_PX;

    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("width", width.toString());
    svg.setAttribute("height", height.toString());
    svg.setAttribute("viewBox", `0 0 ${width} ${height}`);

    const hiddenContainer = document.createElement("div");
    hiddenContainer.style.position = "absolute";
    hiddenContainer.style.visibility = "hidden";
    hiddenContainer.style.width = "0";
    hiddenContainer.style.height = "0";
    document.body.appendChild(hiddenContainer);
    hiddenContainer.appendChild(svg);

    const barcodePromises: Promise<void>[] = [];

    // --- RECT ---
    xmlDoc.querySelectorAll("RectangleShapeItem").forEach((rect) => {
        const x = parseFloat(rect.getAttribute("X")!) * MM_TO_PX;
        const y = parseFloat(rect.getAttribute("Y")!) * MM_TO_PX;
        const w = parseFloat(rect.getAttribute("Width")!) * MM_TO_PX;
        const h = parseFloat(rect.getAttribute("Height")!) * MM_TO_PX;
        const r = parseFloat(rect.getAttribute("CornerRadius")!) * MM_TO_PX || 0;
        const strokeWidth = parseFloat(rect.getAttribute("StrokeThickness")!) * MM_TO_PX || 1;

        const svgRect = document.createElementNS(svg.namespaceURI, "rect");
        svgRect.setAttribute("x", x.toString());
        svgRect.setAttribute("y", y.toString());
        svgRect.setAttribute("width", w.toString());
        svgRect.setAttribute("height", h.toString());
        svgRect.setAttribute("rx", r.toString());
        svgRect.setAttribute("ry", r.toString());
        svgRect.setAttribute("fill", "transparent");
        svgRect.setAttribute("stroke", "#333");
        svgRect.setAttribute("stroke-width", strokeWidth.toString());
        svg.appendChild(svgRect);
    });

    // --- LINE ---
    xmlDoc.querySelectorAll("LineShapeItem").forEach((line) => {
        const x1 = parseFloat(line.getAttribute("X")!) * MM_TO_PX;
        const y1 = parseFloat(line.getAttribute("Y")!) * MM_TO_PX;
        const w = parseFloat(line.getAttribute("Width")!) * MM_TO_PX;
        const h = parseFloat(line.getAttribute("Height")!) * MM_TO_PX;
        const strokeWidth = parseFloat(line.getAttribute("StrokeThickness")!) * MM_TO_PX;

        const svgLine = document.createElementNS(svg.namespaceURI, "line");
        svgLine.setAttribute("x1", x1.toString());
        svgLine.setAttribute("y1", y1.toString());
        svgLine.setAttribute("x2", (x1 + w).toString());
        svgLine.setAttribute("y2", (y1 + h).toString());
        svgLine.setAttribute("stroke", "#333");
        svgLine.setAttribute("stroke-width", strokeWidth.toString());
        svg.appendChild(svgLine);
    });

    // --- TEXT ---
    xmlDoc.querySelectorAll("TextItem").forEach((item) => {
        let x = parseFloat(item.getAttribute("X")!) * MM_TO_PX;
        let y = parseFloat(item.getAttribute("Y")!) * MM_TO_PX;

        const rawText = item.getAttribute("Text") || "";
        const decodedText = decodeEscapedName(rawText);

        if (decodedText === "STC" || decodedText === "BNPI*" || decodedText === "Bifacialy coefficiants") {
            y += 0.5 * MM_TO_PX;
        }
        if (rawText.startsWith("All_x0020_instruction_x0020_should_x0020_be")) {
            y += 2 * MM_TO_PX;
        }
        if (item.getAttribute("Name") === "certificationtext") {
            y += 47;
        }

        const itemWidth = parseFloat(item.getAttribute("Width")!) * MM_TO_PX;
        const fontParts = item.getAttribute("Font")!.split(",");
        const size = parseFloat(fontParts[1]) || 7;
        const fontSize = size * 1.333; // Conversão de pontos para pixels (aproximada)
        const isBold = (fontParts[2] || "").trim().toLowerCase() === 'true';
        const isItalic = (fontParts[3] || "").trim().toLowerCase() === 'true';

        const alignment = item.getAttribute("TextAlignment") || "Left";
        let anchor = "start";
        if (alignment === "Center") {
            x += itemWidth / 2;
            anchor = "middle";
        } else if (alignment === "Right") {
            x += itemWidth;
            anchor = "end";
        }
        if (rawText.startsWith("_x0033_S_x0020_Swiss_x0020_Solar")) {
            x = width / 2;
            anchor = "middle";
        }

        const dataField = item.getAttribute("DataField");
        let value = (dataField && data[dataField] !== undefined)
            ? data[dataField]
            : decodedText;
        if (value === null || value === undefined) value = "";

        const svgText = document.createElementNS(svg.namespaceURI, "text");
        svgText.setAttribute("x", x.toString());
        svgText.setAttribute("y", y.toString());
        svgText.setAttribute("font-size", fontSize.toString());
        svgText.setAttribute("font-family", "Arial, Helvetica, sans-serif");
        svgText.setAttribute("text-anchor", anchor);
        svgText.setAttribute("dominant-baseline", "hanging");

        if (isBold) svgText.setAttribute("font-weight", "bold");
        if (isItalic) svgText.setAttribute("font-style", "italic");

        svg.appendChild(svgText);

        const forceWrap = decodedText.startsWith("WARNING") || decodedText.includes("All instructions");
        if (forceWrap || (!value.includes("\n") && value.length > 50 && itemWidth > 0 && alignment.toLowerCase() !== 'center')) {
            wrapText(svgText, value, itemWidth);
        } else {
            value.split("\n").forEach((line, i) => {
                const tspan = document.createElementNS(svg.namespaceURI, "tspan");
                tspan.setAttribute("x", x.toString());
                tspan.setAttribute("dy", i === 0 ? "0" : "1.2em");
                tspan.textContent = line;
                svgText.appendChild(tspan);
            });
        }
    });

    // --- BARCODE/QRCode ---
    xmlDoc.querySelectorAll("BarcodeItem").forEach((barcode) => {
        const promise = new Promise<void>(async (resolve) => {
            const x = parseFloat(barcode.getAttribute("X")!) * MM_TO_PX;
            const y = parseFloat(barcode.getAttribute("Y")!) * MM_TO_PX;
            const w = (parseFloat(barcode.getAttribute("Width")!) * 0.8) * MM_TO_PX;
            const h = parseFloat(barcode.getAttribute("Height")!) * MM_TO_PX;

            const dataField = barcode.getAttribute("DataField");
            const code = (dataField && data[dataField]) || "123456";

            const canvas = document.createElement("canvas");
            try {
                await (bwipjs as any).toCanvas(canvas, {
                    bcid: 'qrcode',
                    text: code,
                    scale: 5,
                    width: w,
                    height: h
                });

                const svgImage = document.createElementNS(svg.namespaceURI, "image");
                svgImage.setAttribute("href", canvas.toDataURL("image/png"));
                svgImage.setAttribute("x", x.toString());
                svgImage.setAttribute("y", y.toString());
                svgImage.setAttribute("width", w.toString());
                svgImage.setAttribute("height", h.toString());
                svg.appendChild(svgImage);

            } catch (err) {
                console.error("Erro ao gerar barcode:", err);
            }
            resolve();
        });
        barcodePromises.push(promise);
    });

    // --- IMAGE ---
    xmlDoc.querySelectorAll("ImageItem").forEach((image) => {
        let x = parseFloat(image.getAttribute("X")!) * MM_TO_PX;
        let y = parseFloat(image.getAttribute("Y")!) * MM_TO_PX;
        let w = parseFloat(image.getAttribute("Width")!) * MM_TO_PX;
        let h = parseFloat(image.getAttribute("Height")!) * MM_TO_PX;

        const sourceData = image.getAttribute("SourceData");
        if (!sourceData) return;

        const fileName = sourceData.split(/[\\\/]/).pop()!;
        const source = `/images/${fileName}`;

        if (fileName.toLowerCase().includes("cautlogo")) {
            const scale = 0.6;
            x += (w - w * scale) / 2;
            y += (h - h * scale) / 2;
            w *= scale;
            h *= scale;
        }
        if (fileName.toLowerCase().includes("3slogo")) {
            const scale = 0.9;
            x += (w - w * scale) / 2;
            y += (h - h * scale) / 2;
            w *= scale;
            h *= scale;
        }

        const svgImage = document.createElementNS(svg.namespaceURI, "image");
        svgImage.setAttribute("href", source);
        svgImage.setAttribute("x", x.toString());
        svgImage.setAttribute("y", y.toString());
        svgImage.setAttribute("width", w.toString());
        svgImage.setAttribute("height", h.toString());
        svg.appendChild(svgImage);
    });

    await Promise.all(barcodePromises);

    document.body.removeChild(hiddenContainer);

    return svg;
}

export function useParseTL() {
    const [xmlString, setXmlString] = useState<string | null>(null);
    const [svgElement, setSvgElement] = useState<SVGSVGElement | null>(null);

    async function parseTL(file: File) {
        const text = await file.text();
        setXmlString(text);
        const previewSvg = await generateSvg(text, PREVIEW_DATA);
        setSvgElement(previewSvg);
    }


    async function printLabel(data: Record<string, string>) {
        if (!xmlString) {
            alert("Erro: Template file (.tl) not loaded for printing.");
            return;
        }

        const finalSvg = await generateSvg(xmlString, data);

        const printWindow = window.open('', '_blank', 'height=800,width=800');
        if (!printWindow) {
            alert("Pop up blocked. Unable to print");
            return;
        }

        printWindow.document.write(`
          <html>
            <head>
              <title>Printing Label</title>
              <style>
                @media print {
                  @page {
                    size: ${finalSvg.getAttribute('width')}px ${finalSvg.getAttribute('height')}px;
                    margin: 0;
                  }
                  body {
                    margin: 0;
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                  }
                }
              </style>
            </head>
            <body>
            </body>
          </html>
        `);
        printWindow.document.body.appendChild(finalSvg);
        printWindow.document.close();

        setTimeout(() => {
            printWindow.focus();
            printWindow.print();
            printWindow.close();
        }, 500);
    }

    return { svgElement, parseTL, printLabel };
}
